/** @jsxImportSource solid-js */
import type { LinkedElement } from "@previewloop/shared";
import { createSignal, For, Show, type JSX } from "solid-js";
import { WidgetIcon } from "./widget-icon";

export type WidgetLinkedTargetsProps = {
  targets: readonly LinkedElement[];
  onRemove?: (index: number) => void;
  onAdd?: () => void;
  adding?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

/** Ordered target summary shared by the new and saved Capture editors. */
export function WidgetLinkedTargets(props: WidgetLinkedTargetsProps): JSX.Element {
  const [expanded, setExpanded] = createSignal(false);
  const isExpanded = (): boolean => props.expanded ?? expanded();
  const toggleExpanded = (): void => {
    const next = !isExpanded();
    if (props.onExpandedChange) props.onExpandedChange(next);
    else setExpanded(next);
  };

  return (
    <div
      data-pl-linked-targets=""
      class="relative -mt-5 rounded-b-[20px] bg-[oklch(0.975_0.003_252)] px-3 pt-7 pb-2 text-xs"
    >
      <div class="flex items-center gap-2">
        <button
          type="button"
          data-pl-linked-toggle=""
          aria-expanded={isExpanded()}
          class="text-pl-foreground flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 bg-transparent text-left font-medium"
          onClick={toggleExpanded}
        >
          <span>
            {props.targets.length} {props.targets.length === 1 ? "element" : "elements"} linked
          </span>
          <span
            class="grid shrink-0 place-items-center transition-transform duration-150"
            classList={{ "rotate-180": isExpanded() }}
          >
            <WidgetIcon name="chevronDown" size={14} />
          </span>
        </button>
        <Show when={props.onAdd}>
          <button
            type="button"
            data-pl-add-linked=""
            class="text-pl-primary hover:text-pl-foreground cursor-pointer bg-transparent text-xs font-medium whitespace-nowrap"
            onClick={() => props.onAdd?.()}
          >
            {props.adding ? "Click an element…" : "+ Add element"}
          </button>
        </Show>
      </div>
      <Show when={isExpanded()}>
        <div class="mt-2 flex flex-col gap-1">
          <For each={props.targets}>
            {(target, index) => (
              <div class="group text-pl-muted-foreground flex min-w-0 items-center gap-1.5">
                <span class="flex min-w-0 flex-1 items-center gap-1.5">
                  <span class="min-w-0 flex-1 truncate">
                    {target.elementText || target.selector}
                  </span>
                  <Show when={index() === 0}>
                    <span class="bg-pl-primary/10 text-pl-primary shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium">
                      Primary pin
                    </span>
                  </Show>
                </span>
                <Show when={props.onRemove && props.targets.length > 1}>
                  <button
                    type="button"
                    data-pl-linked-remove={index()}
                    aria-label={`Remove linked element ${index() + 1}`}
                    class="hover:text-pl-foreground focus:text-pl-foreground shrink-0 cursor-pointer text-base leading-none opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 focus:opacity-100"
                    onClick={() => props.onRemove?.(index())}
                  >
                    ×
                  </button>
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
